var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * THE HUB — everything about the AI that is not the conversation you are having.
 *
 * One step back from the composer, and deliberately one place rather than three. The things a
 * person wants when they are not mid-sentence are: an earlier thread, what the AI remembers about
 * them, and the numbers. Those are adjacent questions, and whoever is asking one is often about to
 * ask another — so they share a surface instead of hiding behind separate entry points.
 *
 * It OWNS NO CONVERSATION STATE. The list is rendered from the manager's store, which is why the
 * paging, the absolute count and the cache survive this file being rewritten — and they should be,
 * because a list of threads is exactly the sort of thing that gets redesigned.
 *
 * The two links leave for the console, which is where depth lives. Reproducing memory management or
 * usage analysis in a 360px rail would be a worse version of a page that already exists.
 */
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import './conversation-list';
import { LocaleController } from '../i18n/LocaleController';
import '../elements/vitesse-icon';
let VitesseAiHub = class VitesseAiHub extends LitElement {
    constructor() {
        super(...arguments);
        /** Bumped on every store change; the only local state, and it holds no data. */
        this.rev = 0;
        this.onStoreChange = () => {
            this.rev++;
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
    }
    /** LIGHT DOM. The conversation list inside this rail needs the global grid chrome, and a shadow
     *  root does not let global CSS in — which is what forced a hand-copied stylesheet last time. */
    createRenderRoot() {
        return this;
    }
    render() {
        return this.renderList();
    }
    /**
     * THE THREE ACTIONS ARE NOT RENDERED HERE ANY MORE — see `renderHub` in panel.ts.
     *
     * They were a row of their own beneath the header, which was an improvement on the two earlier
     * arrangements (a footer below the list, then three unlabelled icons crammed into the header) but
     * spends a whole 40px band on three controls while the list they sit above is the thing people
     * came for. Every other detail page in this product puts its actions on the RIGHT of its sticky
     * header, and this surface is a detail page; having its own idiom was the anomaly.
     *
     * So the panel renders them, in the header it already owns for all three surfaces — the same
     * mechanism that made the head one row rather than three that look alike. They keep their labels
     * where there is room and collapse to their icons where there is not, which is what makes putting
     * them in a 52px bar survive a 360px rail.
     */
    /**
     * The list is `<vitesse-conversation-list>` — the same element the console's transcript browser
     * renders, in `compact`: a two-line row rather than a table, because this rail is about 360px
     * wide and six columns in 360px is six unreadable stubs. What the two surfaces SHARE is the
     * description of a conversation, which is what stops the same thread reading differently in each.
     */
    renderList() {
        const s = this.store;
        return html `<vitesse-conversation-list
      compact
      .emptyLabel=${s?.failed ? t('ai.unreachable') : ''}
      .rows=${!s || s.pending
            ? null
            : /*
               * A THREAD, THEN WHAT ITS TURNS SET GOING — flattened, with the depth carried on the row.
               *
               * The agents live under the thread that started them rather than beside it, because
               * beside it they are noise that grows with every spawn and underneath it they answer the
               * question the list otherwise cannot: what did that turn start. Flattened rather than
               * nested in the data because the list is virtualised — a row is a row, and depth is a
               * property of it, which keeps windowing and keyboard order working unchanged.
               */
                s.conversations.flatMap((c) => [
                    {
                        id: c.id,
                        title: c.title,
                        atMs: c.updatedAt,
                        messageCount: c.messageCount,
                        preview: c.preview,
                        // Passed THROUGH rather than dropped. The row already knows how to render a cost and
                        // says nothing when there is none, so narrowing here only hid what the reader was
                        // entitled to see — and hid it on the surface they actually use.
                        platform: c.platform,
                        costUsd: c.costUsd,
                        inputTokens: c.inputTokens,
                        outputTokens: c.outputTokens,
                        depth: 0,
                    },
                    ...(c.agents ?? []).map((a) => ({
                        id: a.id,
                        // The run, so the panel can send this click through the canvas. Empty for an agent
                        // recorded before the field existed, which opens its thread directly as before.
                        runId: a.runId ?? '',
                        title: a.label,
                        // ITS OWN, not the parent's. A hard-coded 0 said every agent had gone silent, and
                        // the parent's timestamp said an agent that finished an hour ago had just spoken.
                        atMs: a.atMs || c.updatedAt,
                        messageCount: a.messageCount ?? 0,
                        depth: 1,
                        agent: true,
                    })),
                ])}
      .total=${s?.total ?? -1}
      .onPick=${(id, row) => this.onPick?.(id, row)}
      .onReveal=${(i) => void s?.ensureLoadedThrough(i)}
    ></vitesse-conversation-list>`;
    }
    connectedCallback() {
        super.connectedCallback();
        this.store?.addEventListener('change', this.onStoreChange);
        void this.store?.start();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.store?.removeEventListener('change', this.onStoreChange);
    }
    willUpdate(changed) {
        if (changed.has('store')) {
            changed.get('store')?.removeEventListener('change', this.onStoreChange);
            this.store?.addEventListener('change', this.onStoreChange);
            void this.store?.start();
        }
    }
};
__decorate([
    property({ attribute: false })
], VitesseAiHub.prototype, "store", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiHub.prototype, "onPick", void 0);
__decorate([
    state()
], VitesseAiHub.prototype, "rev", void 0);
VitesseAiHub = __decorate([
    customElement('vitesse-ai-hub')
], VitesseAiHub);
export { VitesseAiHub };
//# sourceMappingURL=hub.js.map